interface ProductAnalysisLike {
    has_file_3d?: boolean;
    dimensions_dict?: Record<string, unknown> | null;
    analysis_status?: { file_3d?: { status?: string; completed?: boolean } | null } | null;
}

/**
 * Whether the 3D file attached now has a Fastlane geometry. Those fields come from the
 * current step file only (2D Fastlane data has other keys), unlike length/width/volume,
 * which stay on the master product after its 3D is removed or replaced.
 */
export const hasFastlane3dGeometry = (product?: ProductAnalysisLike | null) => {
    const dims = product?.dimensions_dict;
    return Boolean(product?.has_file_3d && dims && 'bbox_x' in dims && 'part_volume' in dims);
};

export type DimensionsState = 'show' | 'no-3d' | 'failed' | 'pending' | 'unavailable';

/** What the part header can say about the dimensions of the 3D file attached now. */
export const getDimensionsState = (product?: ProductAnalysisLike | null): DimensionsState => {
    if (!product?.has_file_3d) return 'no-3d';

    const status = product.analysis_status?.file_3d?.status;
    const hasVolume = Boolean(product.dimensions_dict?.volume);
    if (hasVolume && (hasFastlane3dGeometry(product) || status === 'SUCCESS')) return 'show';
    if (status === 'FAILURE') return 'failed';
    if (!product.analysis_status?.file_3d?.completed) return 'pending';
    return hasVolume ? 'show' : 'unavailable';
};

/**
 * A 3D analysis failure only blocks the part when there is no geometry for the file. The
 * detail analysis often fails while the Fastlane geometry, used for pricing, succeeded.
 */
export const isBlocking3dFailure = (product?: ProductAnalysisLike | null) =>
    product?.analysis_status?.file_3d?.status === 'FAILURE' && !hasFastlane3dGeometry(product);

/** The detail analysis failed but the part still has its geometry (and price). */
export const isPartial3dAnalysis = (product?: ProductAnalysisLike | null) =>
    product?.analysis_status?.file_3d?.status === 'FAILURE' && hasFastlane3dGeometry(product);
